import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import type { ActorDatabase, ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import type { FindManyConfig } from "@-/dev.improve.haniverse.lib.database";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { withWriteClockReadGuard } from "@-/dev.improve.haniverse.lib.database.change";
import type { ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import type { QueryClient, QueryFunction } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { stripRelationalConfigColumns } from "#strip-relational-config-columns";
import type { ActorColumnsAgnosticConfig, ActorQueryTable, ActorRelationalRow } from "#types";

// `location` is the caller's `import.meta.location`, threaded in via
// `actorQueries(import.meta.location)`, so `db(location)` attributes query logs
// to the read's origin instead of this factory file. `null` opts out of logging.
export function createActorFindManyQuery<TTable extends ActorQueryTable>(
  location: ImportMetaLocation,
  table: TTable,
) {
  type FindManyQueryConfig = FindManyConfig<ActorDatabaseHandle, TTable>;
  // The joined row for a specific caller config `Cfg`, reconstructed from the
  // actor schema's relational config so `config.with` relations are reflected
  // in the type.
  type RowFor<Cfg extends true | Record<string, unknown>> = ActorRelationalRow<TTable, Cfg>;

  type FindManyQueryKey<
    WithConfig extends FindManyQueryConfig & ActorColumnsAgnosticConfig,
    Config extends FindManyQueryConfig & ActorColumnsAgnosticConfig,
  > = readonly ["actorQuery", TTable, "findMany", WithConfig, Config, { db: ActorDatabase }];

  // The config is column-stripped HERE, the single choke point every key flows
  // through: the query functions read their fetch config back off the key, so
  // stripping at key-build time makes cache entries column-agnostic.
  function useGetQueryKey<
    const WithConfig extends FindManyQueryConfig & ActorColumnsAgnosticConfig,
  >(withConfig: WithConfig) {
    const strippedWithConfig = stripRelationalConfigColumns(withConfig);
    const [db] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
    const withTableChangeVersion = useActorTableChangeVersion(table, withConfig);

    return <const Config extends FindManyQueryConfig & ActorColumnsAgnosticConfig>(
      config: Config,
    ): FindManyQueryKey<WithConfig, Config> =>
      withTableChangeVersion([
        "actorQuery",
        table,
        "findMany",
        strippedWithConfig,
        stripRelationalConfigColumns(config),
        { db },
      ] as const);
  }

  async function queryFn({
    queryKey,
  }: {
    client?: QueryClient;
    queryKey: ReturnType<ReturnType<typeof useGetQueryKey>>;
    // The pair form reads its config at runtime from the key, so its config type
    // isn't statically known — resolve to the base (relation-less) row.
  }): Promise<RowFor<Record<string, never>>[]> {
    const withConfig = queryKey[3];
    const config = queryKey[4];
    const { db } = queryKey[5];
    const queryTable = db(location).query[table];
    // `table` and `config` are paired in the query key; drizzle's per-table
    // `KnownKeysOnly` generic cannot be recovered through the factory parameter.
    /* eslint-disable typescript/no-unsafe-type-assertion -- see above */
    return queryTable.findMany({
      ...withConfig,
      ...config,
    } as Parameters<typeof queryTable.findMany>[0]) as unknown as Promise<
      RowFor<Record<string, never>>[]
    >;
    /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */
  }

  // Every actor read is guarded, including the standalone `queryFn` handed to
  // `client.fetchQuery({ queryFn, queryKey })` composition: a read that spanned a
  // commit to any table it touched re-runs instead of coming to rest as fresh.
  const guardedQueryFn = withWriteClockReadGuard(queryFn);

  /**
   * Builds query options from a render-bound key getter and a lookup-specific
   * config. `useGetQueryKey` captures hooks, the database, and the static
   * relation shape once; this pure function is safe to call later from
   * navigator/prefetch callbacks.
   */
  function getQueryOptions<
    const WithConfig extends FindManyQueryConfig & ActorColumnsAgnosticConfig,
    const Config extends FindManyQueryConfig & ActorColumnsAgnosticConfig,
  >(getQueryKey: (config: Config) => FindManyQueryKey<WithConfig, Config>, config: Config) {
    const queryKey = getQueryKey(config);
    const typedQueryFn: QueryFunction<RowFor<WithConfig & Config>[], typeof queryKey> = async ({
      client,
      queryKey: queryFnQueryKey,
    }) =>
      // The key getter carries the precise static `with` and lookup config types.
      // `queryFn` intentionally exposes the relation-agnostic base type because
      // it can also be called directly without a typed options builder.
      /* eslint-disable typescript/no-unsafe-type-assertion -- refined by the paired key getter above */
      (await guardedQueryFn({ client, queryKey: queryFnQueryKey })) as RowFor<
        WithConfig & Config
      >[];
    /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */

    return { queryFn: typedQueryFn, queryKey };
  }

  // Single-shot entry point. Capturing the literal `Cfg` lets the return type
  // reflect `Cfg["with"]` joins.
  function useQueryOptions<const Cfg extends FindManyQueryConfig & ActorColumnsAgnosticConfig>(
    config: Cfg,
  ) {
    const getQueryKey = useGetQueryKey(config);
    const { queryFn } = getQueryOptions(getQueryKey, config);

    return {
      queryFn,
      get queryKey() {
        return getQueryKey(config);
      },
    };
  }

  return { getQueryOptions, queryFn: guardedQueryFn, useGetQueryKey, useQueryOptions };
}
